import React, { useEffect } from "react";

/**
 * Reusable & Configurable SEO Component
 * Dynamically updates document title, standard meta tags, OpenGraph, Twitter Cards,
 * canonical links, and JSON-LD structured data.
 */
const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = "website",
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
  robots = "index, follow",
  schema,
}) => {
  useEffect(() => {
    // 1. Update Document Title
    const baseTitle = "Simpel — The Cognitive AI Company";
    if (title) {
      document.title = title.includes("Simpel") ? title : `${title} | Simpel`;
    } else {
      document.title = baseTitle;
    }

    // Helper: set or update meta tag by name or property
    const setMeta = (attr, key, val) => {
      if (!val) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", Array.isArray(val) ? val.join(", ") : val);
    };

    // Helper: set or update link tag (canonical)
    const setCanonical = (href) => {
      if (!href) return;
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      // Ensure full absolute URL if relative path provided
      const fullUrl = href.startsWith("http")
        ? href
        : `${window.location.origin}${href.startsWith("/") ? "" : "/"}${href}`;
      link.setAttribute("href", fullUrl);
    };

    // Helper: set JSON-LD schema
    const setSchema = (data) => {
      if (!data) return;
      const scriptId = "seo-json-ld-script";
      let script = document.getElementById(scriptId);
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };

    // 2. Standard Meta Tags
    setMeta("name", "description", description);
    setMeta("name", "keywords", keywords);
    setMeta("name", "robots", robots);

    // 3. Open Graph (Facebook, LinkedIn, Slack, etc.)
    const resolvedUrl =
      ogUrl || (canonical ? (canonical.startsWith("http") ? canonical : `${window.location.origin}${canonical}`) : window.location.href);
    const resolvedImage = ogImage
      ? ogImage.startsWith("http")
        ? ogImage
        : `${window.location.origin}${ogImage.startsWith("/") ? "" : "/"}${ogImage}`
      : undefined;

    setMeta("property", "og:title", ogTitle || title);
    setMeta("property", "og:description", ogDescription || description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:url", resolvedUrl);
    if (resolvedImage) {
      setMeta("property", "og:image", resolvedImage);
    }
    setMeta("property", "og:site_name", "Simpel");

    // 4. Twitter Card
    setMeta("name", "twitter:card", twitterCard);
    setMeta("name", "twitter:title", twitterTitle || ogTitle || title);
    setMeta("name", "twitter:description", twitterDescription || ogDescription || description);
    if (resolvedImage) {
      setMeta("name", "twitter:image", twitterImage || resolvedImage);
    }

    // 5. Canonical URL
    if (canonical) {
      setCanonical(canonical);
    }

    // 6. JSON-LD Structured Data
    if (schema) {
      setSchema(schema);
    }

    // Cleanup when component unmounts (optional: remove structured data script)
    return () => {
      const script = document.getElementById("seo-json-ld-script");
      if (script) {
        script.remove();
      }
    };
  }, [
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    robots,
    schema,
  ]);

  return null; // Headless component
};

export default SEO;

